import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileText, Download, Trash2, PlusCircle, Clock, Users, Eye, X, Loader2, Pencil, History, Calendar } from "lucide-react";
import ListToolbar from "@/components/ListToolbar";

const ISO = (d) => d.toISOString().slice(0, 10);
const PRESETS = { "7d": 7, "30d": 30, "90d": 90 };

export default function QuotationsPage() {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => sessionStorage.getItem("cotflow_q_search") || "");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("cotflow_view_quotations") || "card");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewLoading, setPreviewLoading] = useState(null);
  const [stages, setStages] = useState([]);
  const [versionsModal, setVersionsModal] = useState(null);
  const [versions, setVersions] = useState([]);
  // Filtros de fecha (aplican solo a cotizaciones en etapa final). Persisten en sessionStorage.
  const today = new Date();
  const defaultFrom = new Date(); defaultFrom.setDate(today.getDate() - 30);
  const [dateFrom, setDateFrom] = useState(() => sessionStorage.getItem("cotflow_q_from") || ISO(defaultFrom));
  const [dateTo, setDateTo] = useState(() => sessionStorage.getItem("cotflow_q_to") || ISO(today));
  const [datePreset, setDatePreset] = useState(() => sessionStorage.getItem("cotflow_q_preset") || "30d");
  const [statusFilter, setStatusFilter] = useState(() => sessionStorage.getItem("cotflow_q_status") || "all");
  const navigate = useNavigate();

  const applyPreset = (preset) => {
    setDatePreset(preset); sessionStorage.setItem("cotflow_q_preset", preset);
    if (preset === "all") { setDateFrom(""); setDateTo(""); return; }
    const t = new Date(); const from = new Date();
    if (PRESETS[preset]) from.setDate(t.getDate() - PRESETS[preset]);
    else if (preset === "ytd") { from.setMonth(0); from.setDate(1); }
    setDateFrom(ISO(from)); setDateTo(ISO(t));
  };

  useEffect(() => { sessionStorage.setItem("cotflow_q_search", search); }, [search]);
  useEffect(() => { if (dateFrom) sessionStorage.setItem("cotflow_q_from", dateFrom); else sessionStorage.removeItem("cotflow_q_from"); }, [dateFrom]);
  useEffect(() => { if (dateTo) sessionStorage.setItem("cotflow_q_to", dateTo); else sessionStorage.removeItem("cotflow_q_to"); }, [dateTo]);
  useEffect(() => { sessionStorage.setItem("cotflow_q_status", statusFilter); }, [statusFilter]);

  const load = () => api.get("/quotations").then(r => setQuotations(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => {
    load();
    api.get("/pipeline/stages").then(r => setStages(r.data)).catch(() => {});
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar cotización?")) return;
    await api.delete(`/quotations/${id}`);
    toast.success("Cotización eliminada");
    load();
  };

  const handleDownload = async (q) => {
    if (!q.pdf_url) return toast.error("PDF no generado aún");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotations/${q.id}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("qf_token")}` }
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Cotizacion_${q.title.replace(/\s/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error descargando PDF"); }
  };

  const handleView = async (q) => {
    if (!q.pdf_url) return toast.error("PDF no generado aún");
    setPreviewLoading(q.id);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotations/${q.id}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("qf_token")}` }
      });
      const blob = await res.blob();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewTitle(q.title);
    } catch { toast.error("Error cargando PDF"); }
    finally { setPreviewLoading(null); }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewTitle("");
  };

  const fmtCurrency = (val, cur) => {
    if (!val && val !== 0) return "-";
    const isWhole = val === Math.floor(val);
    if (cur === "CLP") return `$${Math.round(val).toLocaleString("es-CL")}`;
    if (isWhole) {
      const f = Math.floor(val).toLocaleString("es-CL");
      if (cur === "UF") return `${f} UF`;
      if (cur === "EUR") return `€${f}`;
      return `USD $${f}`;
    }
    const f = val.toLocaleString("es-CL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (cur === "UF") return `${f} UF`;
    if (cur === "EUR") return `€${f}`;
    return `USD $${f}`;
  };

  const stageMap = Object.fromEntries(stages.map(s => [s.id, s]));
  const finalIds = new Set(stages.filter(s => s.is_final).map(s => s.id));

  // Fecha en la que entró a la etapa actual (última entrada del historial hacia esa etapa)
  const dateOfCurrentStage = (q) => {
    const hist = q.status_history || [];
    for (let i = hist.length - 1; i >= 0; i--) {
      if (hist[i]?.to_stage === q.status && hist[i]?.timestamp) return new Date(hist[i].timestamp).getTime();
    }
    return q.created_at ? new Date(q.created_at).getTime() : 0;
  };

  const fromTs = dateFrom ? new Date(dateFrom + "T00:00:00").getTime() : null;
  const toTs = dateTo ? new Date(dateTo + "T23:59:59.999").getTime() : null;

  const filtered = quotations.filter(q => {
    const s = search.toLowerCase();
    if (s && !q.title.toLowerCase().includes(s) && !(q.client_name || "").toLowerCase().includes(s)) return false;
    if (statusFilter !== "all" && q.status !== statusFilter) return false;
    // Filtro de fechas SOLO para etapas finales
    if (finalIds.has(q.status)) {
      const ts = dateOfCurrentStage(q);
      if (fromTs && ts < fromTs) return false;
      if (toTs && ts > toTs) return false;
    }
    return true;
  });
  const statusBadge = (status) => {
    const stage = stageMap[status];
    if (stage) return <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold text-white" style={{ backgroundColor: stage.color }}>{stage.name}</span>;
    return <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold bg-slate-100 text-slate-600">{status}</span>;
  };
  const versionBadge = (q) => q.version > 1 ? <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded ml-1">V{q.version}</span> : null;
  const loadVersions = async (q) => {
    setVersionsModal(q);
    try { const r = await api.get(`/quotations/${q.id}/versions`); setVersions(r.data.versions || []); } catch { setVersions([]); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="quotations-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black font-['Satoshi'] tracking-tight text-slate-900">Mis Cotizaciones</h1>
          <p className="text-slate-500 mt-1">{filtered.length} de {quotations.length} cotización{quotations.length !== 1 ? "es" : ""}</p>
        </div>
        <button onClick={() => navigate("/app/quotations/new")} className="rounded-lg bg-slate-900 text-white hover:bg-slate-800 shadow-sm font-medium px-5 py-2.5 transition-all active:scale-95 flex items-center gap-2 text-sm" data-testid="new-quotation-btn">
          <PlusCircle size={18} /> Nueva Cotización
        </button>
      </div>

      <ListToolbar search={search} onSearch={setSearch} viewMode={viewMode} onViewMode={m => { setViewMode(m); localStorage.setItem("cotflow_view_quotations", m); }} />

      <div className="flex items-center gap-3 mb-4 flex-wrap bg-white border border-slate-200 rounded-lg px-4 py-2.5" data-testid="quotations-filters">
        <Calendar size={14} className="text-slate-400" />
        <span className="text-xs text-slate-500 whitespace-nowrap">Cierres entre:</span>
        <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setDatePreset("custom"); }}
          className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="q-date-from" />
        <span className="text-xs text-slate-400">→</span>
        <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setDatePreset("custom"); }}
          className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="q-date-to" />
        <div className="flex items-center gap-1 ml-1">
          {[["7d","7 días"],["30d","30 días"],["90d","90 días"],["ytd","Año"],["all","Todo"]].map(([k, lbl]) => (
            <button key={k} onClick={() => applyPreset(k)}
              className={`text-[11px] px-2 py-1 rounded-md font-medium transition-all ${datePreset === k ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              data-testid={`q-preset-${k}`}>{lbl}</button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 ml-auto">
          <label className="text-xs text-slate-500">Estado:</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="q-status-filter">
            <option value="all">Todos</option>
            {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <span className="text-[10px] text-slate-400 italic w-full">El filtro de fecha aplica solo a cotizaciones en etapas de cierre</span>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <FileText size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium text-lg">{search ? "Sin resultados" : "Aún no tienes cotizaciones"}</p>
          {!search && <p className="text-sm text-slate-400 mt-1">Crea tu primera cotización para empezar</p>}
          {!search && (
            <button onClick={() => navigate("/app/quotations/new")} className="mt-4 bg-blue-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-all" data-testid="create-first-quotation">
              Crear cotización
            </button>
          )}
        </div>
      ) : viewMode === "card" ? (
        <div className="space-y-3">
          {filtered.map(q => (
            <div key={q.id} className="bg-white rounded-xl border border-slate-200 px-6 py-5 shadow-sm hover:shadow-md transition-all flex items-center justify-between" data-testid={`quotation-${q.id}`}>
              <div className="cursor-pointer flex-1" onClick={() => q.pdf_url && handleView(q)}>
                <div className="font-bold text-slate-900">{q.title}{versionBadge(q)}</div>
                <div className="flex items-center gap-4 mt-1.5 text-sm text-slate-500">
                  <span className="flex items-center gap-1"><Users size={14} /> {q.client_name}</span>
                  <span className="flex items-center gap-1"><Clock size={14} /> {new Date(q.created_at).toLocaleDateString("es-CL")}</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="font-bold text-slate-900 text-lg">{fmtCurrency(q.total, q.currency)}</div>
                  {statusBadge(q.status)}
                </div>
                {q.pdf_url && (
                  <>
                    <button onClick={() => handleView(q)} disabled={previewLoading === q.id} className="rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 p-2.5 transition-all" data-testid={`view-${q.id}`}>
                      {previewLoading === q.id ? <Loader2 size={18} className="animate-spin" /> : <Eye size={18} />}
                    </button>
                    <button onClick={() => handleDownload(q)} className="rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 p-2.5 transition-all" data-testid={`download-${q.id}`}>
                      <Download size={18} />
                    </button>
                  </>
                )}
                {q.status === "creation" && (
                  <button onClick={() => navigate(`/app/quotations/edit/${q.id}`)} className="rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 p-2.5 transition-all" data-testid={`edit-${q.id}`} title="Editar">
                    <Pencil size={18} />
                  </button>
                )}
                {(q.versions?.length > 0 || q.version > 1) && (
                  <button onClick={() => loadVersions(q)} className="rounded-lg bg-violet-50 text-violet-600 hover:bg-violet-100 p-2.5 transition-all" data-testid={`versions-${q.id}`} title="Ver versiones">
                    <History size={18} />
                  </button>
                )}
                <button onClick={() => handleDelete(q.id)} className="rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 p-2.5 transition-all" data-testid={`delete-q-${q.id}`}>
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Título</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Cliente</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Fecha</th>
              <th className="text-right px-5 py-3 font-semibold text-slate-600">Total</th>
              <th className="text-center px-5 py-3 font-semibold text-slate-600">Estado</th>
              <th className="px-5 py-3"></th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(q => (
                <tr key={q.id} className="hover:bg-slate-50/50" data-testid={`quotation-row-${q.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-900 cursor-pointer hover:text-blue-600" onClick={() => q.pdf_url && handleView(q)}>{q.title}{versionBadge(q)}</td>
                  <td className="px-5 py-3 text-slate-500">{q.client_name}</td>
                  <td className="px-5 py-3 text-slate-500">{new Date(q.created_at).toLocaleDateString("es-CL")}</td>
                  <td className="px-5 py-3 text-right font-bold text-slate-900">{fmtCurrency(q.total, q.currency)}</td>
                  <td className="px-5 py-3 text-center">{statusBadge(q.status)}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {q.pdf_url && (
                        <>
                          <button onClick={() => handleView(q)} disabled={previewLoading === q.id} className="text-slate-500 hover:text-blue-600 p-1" data-testid={`view-row-${q.id}`}>
                            {previewLoading === q.id ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}
                          </button>
                          <button onClick={() => handleDownload(q)} className="text-blue-500 hover:text-blue-700 p-1" data-testid={`download-row-${q.id}`}><Download size={16} /></button>
                        </>
                      )}
                      {q.status === "creation" && <button onClick={() => navigate(`/app/quotations/edit/${q.id}`)} className="text-amber-500 hover:text-amber-700 p-1" title="Editar"><Pencil size={16} /></button>}
                      {(q.versions?.length > 0 || q.version > 1) && <button onClick={() => loadVersions(q)} className="text-violet-500 hover:text-violet-700 p-1" title="Versiones"><History size={16} /></button>}
                      <button onClick={() => handleDelete(q.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="quotation-preview-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold font-['Satoshi'] text-slate-900">{previewTitle}</h2>
              <button onClick={closePreview} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-testid="close-quotation-preview"><X size={20} /></button>
            </div>
            <div className="flex-1 p-4 bg-slate-100 overflow-hidden rounded-b-2xl">
              <iframe src={previewUrl} className="w-full h-full rounded-lg border border-slate-200 bg-white" title="Quotation PDF" data-testid="quotation-preview-iframe" />
            </div>
          </div>
        </div>
      )}

      {/* Versions Modal */}
      {versionsModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setVersionsModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="versions-modal">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Historial de Versiones</h3>
                <p className="text-sm text-slate-500 truncate">{versionsModal.title} <span className="font-mono font-bold text-blue-600">V{versionsModal.version || 1}</span></p>
              </div>
              <button onClick={() => setVersionsModal(null)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X size={18} /></button>
            </div>
            <div className="p-6">
              {versions.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">Sin versiones anteriores guardadas</p>
              ) : (
                <div className="space-y-3">
                  {versions.slice().reverse().map((v, i) => (
                    <div key={i} className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono font-bold text-blue-600 text-sm">Versión {v.version}</span>
                        <span className="text-[10px] text-slate-400">{new Date(v.created_at).toLocaleString("es-CL")}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-slate-50 rounded p-2"><span className="text-slate-400">Total:</span> <span className="font-bold text-slate-700">{fmtCurrency(v.data?.total, v.data?.currency)}</span></div>
                        <div className="bg-slate-50 rounded p-2"><span className="text-slate-400">Items:</span> <span className="font-bold text-slate-700">{v.data?.items?.length || 0}</span></div>
                      </div>
                      {v.data?.items?.length > 0 && (
                        <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                          {v.data.items.map((item, ii) => (
                            <div key={ii} className="flex justify-between">
                              <span className="truncate flex-1">{item.service_name}</span>
                              <span className="font-medium text-slate-700 ml-2">{fmtCurrency(item.quantity * item.unit_price, v.data?.currency)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
